import { memo, useEffect, useState } from "react";
import type { FC } from "react";
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { useReactFlow } from "@xyflow/react";
import { Outcome } from "@unlikelyai-magic/shared/data-access/api";
import { Paragraph } from "@unlikelyai-magic/ui/typography";
import { BaseNode, BaseNodeRenderData } from "./BaseNode";

/**
 * Props for ConditionNode data.
 * The leafValue and leafValueExplanation can be provided directly in the data
 * instead of relying on Redux selectors.
 */
export interface ConditionNodeData extends BaseNodeRenderData {
  label: string;
  question: string;
  value: Outcome;
  /**
   * Optional leaf value override. If provided, takes precedence over data.value.
   */
  leafValue?: Outcome;
  /**
   * Optional explanation for the leaf value evaluation.
   */
  leafValueExplanation?: string;
}

export interface ConditionNodeProps {
  id: any;
  data: ConditionNodeData;
  isConnectable: any;
}

/**
 * ConditionNode component for displaying leaf conditions in the graph.
 * Accepts leafValue and leafValueExplanation via data props to support
 * both static values and dynamic values from external state (e.g., Redux).
 */
const ConditionNodeComponent: FC<ConditionNodeProps> = ({
  id,
  data,
  isConnectable,
}) => {
  // Use provided leafValue from data or fall back to data.value
  const leafValue = data.leafValue ?? data.value;
  const leafValueExplanation = data.leafValueExplanation;

  const { updateNodeData } = useReactFlow();

  // Derive outcome directly from leafValue for immediate visual updates
  const outcome = leafValue ?? data.value ?? Outcome.UNDECIDED;

  // Hover-controlled tooltip that matches the node width
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "top",
    middleware: [offset(8)],
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(context, { move: false, restMs: 50 });
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    role,
  ]);

  // Sync node data when leafValue changes
  useEffect(() => {
    updateNodeData(id, { value: leafValue || Outcome.UNDECIDED });
  }, [leafValue, id, updateNodeData]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        <BaseNode
          id={id}
          data={data}
          isConnectable={isConnectable}
          outcome={outcome}
        >
          <Paragraph
            color={outcome === Outcome.UNDECIDED ? undefined : "white"}
            className="font-semibold"
          >
            {data.question}
          </Paragraph>
        </BaseNode>
      </div>
      {open &&
        leafValueExplanation &&
        leafValue !== Outcome.UNDECIDED &&
        leafValue && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="max-w-sm rounded-lg p-2 bg-white border border-gray-200 shadow-lg whitespace-pre-wrap break-words z-10"
            >
              <h5 className="text-sm font-semibold">
                Explanation for evaluation
              </h5>
              <Paragraph small>{leafValueExplanation}</Paragraph>
            </div>
          </FloatingPortal>
        )}
    </>
  );
};

/**
 * Memoized ConditionNode with custom comparison that checks leafValue.
 * This ensures the component re-renders when Redux state changes.
 */
export const ConditionNode = memo(
  ConditionNodeComponent,
  (prevProps, nextProps) => {
    // Re-render if leafValue, leafValueExplanation, or value changes
    if (prevProps.data.leafValue !== nextProps.data.leafValue) return false;
    if (
      prevProps.data.leafValueExplanation !==
      nextProps.data.leafValueExplanation
    )
      return false;
    if (prevProps.data.value !== nextProps.data.value) return false;
    if (prevProps.id !== nextProps.id) return false;
    // Otherwise, use default shallow comparison for other data properties
    return (
      prevProps.data === nextProps.data &&
      prevProps.isConnectable === nextProps.isConnectable
    );
  }
);
